import React, { forwardRef } from "react";
import {
  extractYoutubeId,
  extractVimeoId,
  getVideoType,
} from "./utils/videoUtils";

interface VideoPlayerProps {
  url: string;
  className?: string;
  title?: string;
}

const VideoPlayer = forwardRef<
  HTMLIFrameElement | HTMLVideoElement,
  VideoPlayerProps
>(({ url, className = "w-full h-full", title = "Video player" }, ref) => {
  const videoType = getVideoType(url);

  if (!videoType) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400`}
      >
        <p>Invalid video URL</p>
      </div>
    );
  }

  switch (videoType) {
    case "youtube":
      const youtubeId = extractYoutubeId(url);
      return (
        <iframe
          ref={ref as React.Ref<HTMLIFrameElement>}
          src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&origin=${window.location.origin}&rel=0&modestbranding=1&playsinline=1`}
          title={title || "YouTube video player"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className={className}
        />
      );

    case "vimeo":
      const vimeoId = extractVimeoId(url);
      return (
        <iframe
          ref={ref as React.Ref<HTMLIFrameElement>}
          src={`https://player.vimeo.com/video/${vimeoId}`}
          title={title || "Vimeo video player"}
          allow="autoplay; fullscreen; picture-in-picture"
          className={className}
        />
      );

    case "custom":
      return (
        <video
          ref={ref as React.Ref<HTMLVideoElement>}
          src={url}
          title={title || "Custom video player"}
          controls
          className={`${className} object-contain`}
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
      );

    default:
      return (
        <div
          className={`${className} flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400`}
        >
          <p>Unsupported video format</p>
        </div>
      );
  }
});

VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
